import { api } from "../../api";
import type { DisplayConversation, ShareInfo } from "./ChatPage";
import {
  applyConversationStreamEvent,
  isAbortError,
  streamRequest,
} from "./ChatPage.utils";
import { PROJECTS_CHANGED_EVENT } from "../../components/projects/projectEvents";
import type { ChatConversationActionsContext } from "./ChatContextTypes";
import { resolveShareInfo } from "./shareLinkCache";

export function useChatConversationActions(
  context: ChatConversationActionsContext,
) {
  const {
    activeRequest,
    applyConversationSnapshot,
    compacting,
    conversation,
    conversationRef,
    deleteTarget,
    navigate,
    refreshConversations,
    setActionBusy,
    setCompacting,
    setConversation,
    setDeleteConversationOpen,
    setDeleteTarget,
    setError,
    setRenameOpen,
    setSending,
    setShareInfo,
    setShareOpen,
    setTitleDraft,
    sending,
    titleDraft,
    updateConversation,
  } = context;
  const remove = async () => {
    const current = conversationRef.current;
    if (!current || !deleteTarget) return;
    setSending(true);
    setError("");
    try {
      setConversation(
        await api<DisplayConversation>(
          `/conversations/${current.id}/messages/${deleteTarget.id}`,
          { method: "DELETE" },
        ),
      );
      setDeleteTarget(null);
      await refreshConversations();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not delete message",
      );
    } finally {
      setSending(false);
    }
  };
  const openRename = () => {
    if (!conversation) return;
    setTitleDraft(conversation.title);
    setRenameOpen(true);
  };
  const renameConversation = async () => {
    if (!conversation || !titleDraft.trim()) return;
    setActionBusy(true);
    setError("");
    try {
      const updated = await api<DisplayConversation>(
        `/conversations/${conversation.id}`,
        { method: "PATCH", body: JSON.stringify({ title: titleDraft.trim() }) },
      );
      setConversation((current) =>
        current
          ? { ...current, title: updated.title, updatedAt: updated.updatedAt }
          : current,
      );
      if (conversation.surface === "project")
        window.dispatchEvent(new Event(PROJECTS_CHANGED_EVENT));
      setRenameOpen(false);
      await refreshConversations();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not rename chat",
      );
    } finally {
      setActionBusy(false);
    }
  };
  const openShare = async () => {
    if (!conversation) return;
    setShareOpen(true);
    setShareInfo(null);
    setActionBusy(true);
    try {
      setShareInfo(
        resolveShareInfo(
          conversation.id,
          await api<ShareInfo>(`/conversations/${conversation.id}/share`),
        ),
      );
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not load sharing settings",
      );
      setShareOpen(false);
    } finally {
      setActionBusy(false);
    }
  };
  const enableShare = async () => {
    if (!conversation) return;
    setActionBusy(true);
    try {
      setShareInfo(
        resolveShareInfo(
          conversation.id,
          await api<ShareInfo>(`/conversations/${conversation.id}/share`, {
            method: "POST",
            body: JSON.stringify({}),
          }),
        ),
      );
      setConversation((current) =>
        current ? { ...current, shared: true } : current,
      );
      await refreshConversations();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not share chat",
      );
    } finally {
      setActionBusy(false);
    }
  };
  const disableShare = async () => {
    if (!conversation) return;
    setActionBusy(true);
    try {
      await api<void>(`/conversations/${conversation.id}/share`, {
        method: "DELETE",
      });
      setShareInfo(
        resolveShareInfo(conversation.id, {
          enabled: false,
          token: null,
          sharedAt: null,
        }),
      );
      setConversation((current) =>
        current ? { ...current, shared: false } : current,
      );
      await refreshConversations();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not disable sharing",
      );
    } finally {
      setActionBusy(false);
    }
  };
  const deleteConversation = async () => {
    if (!conversation) return;
    setActionBusy(true);
    setError("");
    try {
      await api<void>(`/conversations/${conversation.id}`, {
        method: "DELETE",
      });
      if (conversation.surface === "project")
        window.dispatchEvent(new Event(PROJECTS_CHANGED_EVENT));
      setDeleteConversationOpen(false);
      await refreshConversations();
      navigate(conversation.surface === "project" ? "/projects" : "/", {
        replace: true,
      });
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not delete chat",
      );
    } finally {
      setActionBusy(false);
    }
  };
  const compactConversation = async () => {
    const current = conversationRef.current;
    if (!current || sending || compacting) return;
    const controller = new AbortController();
    activeRequest.current = controller;
    setCompacting(true);
    setError("");
    const assistantMessageId = { current: null as string | null };
    try {
      await streamRequest(
        `/conversations/${current.id}/compact`,
        {},
        (event) => {
          applyConversationStreamEvent(event, {
            updateConversation,
            setConversation,
            assistantMessageId,
            applyConversationSnapshot,
            conversationId: current.id,
          });
          if (event.type === "error") {
            setError(event.error);
          }
        },
        controller.signal,
        { isStale: () => conversationRef.current?.id !== current.id },
      );
    } catch (reason) {
      if (isAbortError(reason)) return;
      setError(
        reason instanceof Error ? reason.message : "Context compaction failed",
      );
    } finally {
      if (activeRequest.current === controller) activeRequest.current = null;
      if (conversationRef.current?.id === current.id) setCompacting(false);
    }
  };
  return {
    remove,
    openRename,
    renameConversation,
    openShare,
    enableShare,
    disableShare,
    deleteConversation,
    compactConversation,
  };
}
